import { useIsMobile } from '@brunommsilva/react-foundations';
import {
  Box,
  CardsList,
  Currency,
  Item,
  Table,
} from '@brunommsilva/react-foundations';
import { Box as MuiBox, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TRANSACTIONS_PATH } from '../../config/paths';
import { TEST_IDS } from '../../config/test-ids';
import { TransactionsFilter } from '../../hooks/transactions/filter/filter';
import TransactionsFilterStorage from '../../hooks/transactions/filter/storage';
import { Account } from '../../models/accounts';
import { Delete, Edit } from '../icons';
import AccountTypeChip from './account-type-chip';
import DeleteAccountModal from './delete-account-modal';
import EditAccountModal from './edit-account-modal';

interface AccountsTableProps {
  accounts: Account[];
  isLoading: boolean;
  isError?: boolean;
}

const AccountsTable = ({
  accounts,
  isLoading,
  isError,
}: AccountsTableProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [currentAccount, setCurrentAccount] = useState<Account>();
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);

  const handleRowClick = (id: string) => {
    const account = accounts.find((account) => account.id === id);
    if (!account) {
      return;
    }

    const accountFilter = new TransactionsFilter({
      account: {
        id: account.id,
        name: account.name,
      },
    });
    TransactionsFilterStorage.set(accountFilter);
    navigate(TRANSACTIONS_PATH);
  };

  const onDeleteAccount = (account: Account) => {
    setCurrentAccount(account);
    setShowDeleteAccountModal(true);
  };

  const closeDeleteAccountModal = () => {
    setShowDeleteAccountModal(false);
  };

  const onEditAccount = (account: Account) => {
    setCurrentAccount(account);
    setShowEditAccountModal(true);
  };

  const closeEditAccountModal = () => {
    setShowEditAccountModal(false);
  };

  const buildColumns = () => {
    return [
      {
        id: 'ACCOUNT_NAME',
        label: t('ACCOUNT_NAME'),
        format: (account: Account) => (
          <span data-testid={TEST_IDS.accounts.name}>{account.name}</span>
        ),
      },
      {
        id: 'ACCOUNT_TYPE',
        label: t('ACCOUNT_TYPE'),
        format: (account: Account) => <AccountTypeChip type={account.type} />,
      },
      {
        id: 'ACCOUNT_BALANCE',
        label: t('ACCOUNT_BALANCE'),
        align: 'right',
        format: (account: Account) => (
          <span data-testid={TEST_IDS.accounts.balance}>
            <Currency amount={account.balance} />
          </span>
        ),
      },
      {
        id: 'ACTIONS',
        align: 'right',
        format: (account: Account) => (
          <>
            <IconButton
              color="primary"
              data-testid={TEST_IDS.accounts.editButton}
              onClick={(e) => {
                e.stopPropagation();
                onEditAccount(account);
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              data-testid={TEST_IDS.accounts.deleteButton}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteAccount(account);
              }}
            >
              <Delete />
            </IconButton>
          </>
        ),
      },
    ];
  };

  const renderDeleteAccountModal = () => {
    return (
      <DeleteAccountModal
        show={showDeleteAccountModal}
        account={currentAccount}
        onDeleted={closeDeleteAccountModal}
        onClose={closeDeleteAccountModal}
      />
    );
  };

  const renderEditAccountModal = () => {
    return (
      <EditAccountModal
        show={showEditAccountModal}
        account={currentAccount}
        onEdited={closeEditAccountModal}
        onClose={closeEditAccountModal}
      />
    );
  };

  const renderEmpty = () => (
    <Box direction="column" alignItems="center">
      <Item>{t('NO_ACCOUNTS_FOUND_MESSAGE')}</Item>
    </Box>
  );

  const renderCards = () => (
    <CardsList
      isLoading={isLoading}
      isError={isError}
      items={accounts}
      renderItem={(account) => (
        <MuiBox
          key={account.id}
          onClick={() => handleRowClick(account.id)}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'background.paper',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {/* Left Column */}
          <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {account.name}
            </Typography>
            <MuiBox>
              <AccountTypeChip type={account.type} />
            </MuiBox>
          </MuiBox>

          {/* Right Column */}
          <MuiBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
              }}
            >
              <Currency amount={account.balance} />
            </Typography>
            <MuiBox sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditAccount(account);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteAccount(account);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </MuiBox>
          </MuiBox>
        </MuiBox>
      )}
    />
  );

  const renderTable = () => (
    <Table
      isLoading={isLoading}
      isError={isError}
      columns={buildColumns()}
      rows={accounts}
      onRowClick={handleRowClick}
    />
  );

  const renderList = () => {
    if (!isLoading && !isError && (!accounts || accounts.length === 0)) {
      return renderEmpty();
    }
    if (isMobile) {
      return renderCards();
    }
    return renderTable();
  };

  return (
    <>
      {renderList()}
      {renderEditAccountModal()}
      {renderDeleteAccountModal()}
    </>
  );
};

export default AccountsTable;
